/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useIsMobile } from "@/utils/useIsMobile";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import icon from "../icons/Icons";

interface ModalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  maxSize?: string;
  transitionSpeed?: number;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  useDragToClose?: boolean;
  isBlurOverlay?: boolean;
  mobileCondition?: number;
  overlayBg?: string;
}

// Every time this custom modal is used, the callback function must be triggered either in the close button or the close function to ensure the input fields function properly.

// transitionSpeed controls the speed of the Modal's transition:
// Higher values result in faster transitions, while lower values make the transition slower.

export default function CustomModal({
  isOpen,
  onClose,
  isBlurOverlay = false,
  mobileCondition = 769,
  children,
  maxSize,
  className,
  closeOnOverlayClick = true,
  showCloseButton = true,
  useDragToClose = false,
  transitionSpeed = 200,
  overlayBg = "bg-black/60",
}: ModalDrawerProps) {
  const isMobile = useIsMobile(mobileCondition);

  const drawerRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const translateY = useRef(0);
  const isDragging = useRef(false);

  const handleSafeClose = () => {
    // unlockBodyScroll();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
      return () => {
        unlockBodyScroll(); // only runs when modal closes or unmounts
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isMobile || !isOpen || !useDragToClose) return;

    const handle = dragHandleRef.current;
    const drawer = drawerRef.current;
    if (!handle || !drawer) return;

    const onStart = (e: TouchEvent | MouseEvent) => {
      isDragging.current = true;
      startY.current = "touches" in e ? e.touches[0].clientY : e.clientY;
      drawer.style.transition = "none";
    };

    const onMove = (e: TouchEvent | MouseEvent) => {
      if (!isDragging.current) return;
      currentY.current = "touches" in e ? e.touches[0].clientY : e.clientY;
      translateY.current = currentY.current - startY.current;
      if (translateY.current > 0) {
        drawer.style.transform = `translateY(${translateY.current}px)`;
      }
    };

    const onEnd = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      drawer.style.transition = "";
      if (translateY.current > 100) {
        handleSafeClose();
      } else {
        drawer.style.transform = "translateY(0)";
      }
      translateY.current = 0;
    };

    handle.addEventListener("touchstart", onStart);
    handle.addEventListener("touchmove", onMove);
    handle.addEventListener("touchend", onEnd);

    handle.addEventListener("mousedown", onStart);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);

    return () => {
      handle.removeEventListener("touchstart", onStart);
      handle.removeEventListener("touchmove", onMove);
      handle.removeEventListener("touchend", onEnd);

      handle.removeEventListener("mousedown", onStart);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
    };
  }, [isOpen, isMobile, useDragToClose]);

  useEffect(() => {
    if (!isOpen) return;

    const focusableElements = drawerRef.current?.querySelectorAll<HTMLElement>(
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );

    const firstElement = focusableElements?.[0];
    const lastElement = focusableElements?.[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDragging.current) return;
      if (e.key === "Tab" && focusableElements && firstElement && lastElement) {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      } else if (e.key === "Escape") {
        handleSafeClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    firstElement?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) handleSafeClose();
  };

  const CloseButton = () =>
    showCloseButton && (
      <button
        onClick={handleSafeClose}
        aria-label="Close Popup"
        className={`group absolute -top-14 ${isMobile ? "left-0 right-0 mx-auto" : "right-0"} w-10 h-10 bg-white rounded-full flex items-center justify-center`}
      >
        <icon.popupCloseIcon className="w-5 h-5 text-primaryBlack group-hover:text-(--text) rotate-45 transition" />
      </button>
    );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className={[
              "fixed inset-0 z-max flex justify-center m-0!",
              overlayBg,
              isMobile ? "items-end" : "items-center",
              isBlurOverlay ? "backdrop-blur-xs" : "",
            ].join(" ")}
            style={
              isBlurOverlay
                ? {
                    transform: "translateZ(0)",
                    willChange: "opacity",
                    contain: "paint",
                    isolation: "isolate",
                  }
                : undefined
            }
            onClick={handleOverlayClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {isMobile ? (
              // Mobile Drawer
              <>
                <motion.div
                  onClick={(e) => e.stopPropagation()}
                  className="relative z-1 bottom-0 left-0 right-0 w-full"
                  initial={{ opacity: 0, y: "100%" }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: "100%" }}
                  transition={{
                    type: "spring",
                    stiffness: transitionSpeed,
                    damping: 30,
                  }}
                >
                  {/* Drag handle */}
                  <div ref={drawerRef} className="relative">
                    <CloseButton />

                    <div
                      className={`relative w-full bg-white rounded-t-4xl shadow-xl max-h-[70vh] overflow-y-auto flex flex-col scrollbar-none ${className}`}
                    >
                      {useDragToClose && (
                        <div
                          ref={dragHandleRef}
                          className="sticky top-0 z-1 bg-inherit py-4"
                        >
                          <div className="mx-auto w-16 h-1 bg-white mix-blend-difference rounded-lg" />
                        </div>
                      )}
                      {typeof children === "function"
                        ? (children as any)({ onClose: handleSafeClose })
                        : children}
                    </div>
                  </div>
                </motion.div>
              </>
            ) : (
              // Desktop Modal
              <>
                <motion.div
                  ref={drawerRef}
                  onClick={(e) => e.stopPropagation()}
                  className={`relative z-1 w-full ${maxSize}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{
                    type: "spring",
                    stiffness: transitionSpeed,
                    damping: 30,
                  }}
                >
                  <CloseButton />
                  <div className={`relative bg-white rounded-4xl ${className}`}>
                    {" "}
                    {typeof children === "function"
                      ? (children as any)({ onClose: handleSafeClose })
                      : children}
                  </div>
                </motion.div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
